"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { ImageOff } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { CollectionListingItem } from "@/features/collections/types";
import { Avatar, StatePanel } from "@/features/home-v2/ui";
import { formatTokenSymbol } from "@/lib/asset-naming";
import { sanitizeUrl } from "@/lib/helpers";
import { LISTINGS_PAGE_SIZE, useListingsPage } from "../data/useDetailData";
import Pager from "../Pager";
import FitHex from "../FitHex";
import { dateTime, RetryButton, RowsSkeleton, tokenAmount, tokenDigits } from "../parts";
type Props = {
  contract: string;
  lang: "fa" | "en";
  t: Dictionary["collectionPage"];
  states: Dictionary["states"];
  emptyAction: ReactNode;
};

/** Where the listing lives, in words a reader knows. */
function marketLabel(marketplace: string, isFa: boolean) {
  const m = marketplace.toLowerCase();
  if (m === "nftbaz") return isFa ? "ان‌اف‌تی‌باز" : "NFTBaz";
  if (m === "onchain") return isFa ? "روی زنجیره" : "On chain";
  return marketplace;
}

/**
 * THE WORKS ON SALE — `GET /collections/{contract}/listings`, one row per
 * NFT, newest first: the work (its picture and name, a link to its page),
 * the asking price in the listing's own currency, the seller (a known
 * account by name, otherwise the wallet), where it is listed and when.
 */
export default function VitrineListings(t0) {
  const $ = _c(42);
  const {
    contract,
    lang,
    t,
    states,
    emptyAction
  } = t0;
  const isFa = lang === "fa";
  const locale = isFa ? "fa-IR" : "en-US";
  const l = useListingsPage(contract, lang);
  if (l.isLoading && l.listings.length === 0) {
    let t1;
    if ($[0] !== states.loading) {
      t1 = <RowsSkeleton label={states.loading} rows={LISTINGS_PAGE_SIZE} />;
      $[0] = states.loading;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (l.listings.length === 0) {
    let t1;
    if ($[2] !== emptyAction || $[3] !== l.isSuccess || $[4] !== l.retry || $[5] !== states || $[6] !== t) {
      t1 = l.isSuccess ? <StatePanel title={t.listingsEmptyTitle} body={t.listingsEmptyBody} action={emptyAction} /> : <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={l.retry} />} />;
      $[2] = emptyAction;
      $[3] = l.isSuccess;
      $[4] = l.retry;
      $[5] = states;
      $[6] = t;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t1;
  if ($[8] !== l.total || $[9] !== locale || $[10] !== t.listingsCount) {
    t1 = l.total != null ? <p className="vt-count" aria-live="polite"><b>{new Intl.NumberFormat(locale).format(l.total)}</b> {t.listingsCount}</p> : null;
    $[8] = l.total;
    $[9] = locale;
    $[10] = t.listingsCount;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const t2 = t.listingsTableLabel;
  const t3 = l.isFetching || undefined;
  let t4;
  if ($[12] !== l.listings || $[13] !== lang || $[14] !== locale || $[15] !== t) {
    let t5;
    if ($[17] !== lang || $[18] !== locale || $[19] !== t) {
      t5 = row => <ListingRow key={row.id} row={row} lang={lang} locale={locale} t={t} />;
      $[17] = lang;
      $[18] = locale;
      $[19] = t;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    t4 = l.listings.map(t5);
    $[12] = l.listings;
    $[13] = lang;
    $[14] = locale;
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[21] !== t.listingsTableLabel || $[22] !== t3 || $[23] !== t4) {
    t5 = <ul className="vt-lists" aria-label={t2} data-busy={t3}>{t4}</ul>;
    $[21] = t.listingsTableLabel;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== isFa || $[26] !== l.isFetching || $[27] !== l.lastPage || $[28] !== l.page || $[29] !== l.setPage) {
    t6 = <Pager page={l.page} last={l.lastPage} busy={l.isFetching} onPage={l.setPage} isFa={isFa} />;
    $[25] = isFa;
    $[26] = l.isFetching;
    $[27] = l.lastPage;
    $[28] = l.page;
    $[29] = l.setPage;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== l.backgroundFailure || $[32] !== l.retry || $[33] !== states) {
    t7 = l.backgroundFailure ? <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={l.retry} />} /> : null;
    $[31] = l.backgroundFailure;
    $[32] = l.retry;
    $[33] = states;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== t6 || $[36] !== t7) {
    t8 = <div className="v2-cd-foot">{t6}{t7}</div>;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== t1 || $[39] !== t5 || $[40] !== t8) {
    t9 = <div className="vt-stack">{t1}{t5}{t8}</div>;
    $[38] = t1;
    $[39] = t5;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
}
function ListingRow(t0) {
  const $ = _c(64);
  const {
    row,
    lang,
    locale,
    t
  } = t0;
  const isFa = lang === "fa";
  const tokenId = String(row.nft?.token_id ?? row.token_id);
  const address = row.nft?.contract_address || row.contract_address;
  const href = `/${lang}/assets/${row.chain || "ethereum"}/${address}/${tokenId}`;
  let t1;
  if ($[0] !== row.nft?.image) {
    t1 = sanitizeUrl(row.nft?.image ?? undefined) || undefined;
    $[0] = row.nft?.image;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const image = t1;
  let t2;
  if ($[2] !== isFa || $[3] !== row.nft?.name || $[4] !== tokenId) {
    t2 = row.nft?.name?.trim() || `#${tokenDigits(tokenId, isFa)}`;
    $[2] = isFa;
    $[3] = row.nft?.name;
    $[4] = tokenId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const name = t2;
  const t3 = row.price_per_item ?? row.price;
  let t4;
  if ($[6] !== locale || $[7] !== t3) {
    t4 = tokenAmount(t3, locale);
    $[6] = locale;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const price = t4;
  const t5 = row.currency?.symbol;
  let t6;
  if ($[9] !== t5) {
    t6 = formatTokenSymbol(t5);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const symbol = t6;
  let t7;
  if ($[11] !== row.user?.name || $[12] !== row.user?.username) {
    t7 = row.user?.name?.trim() || row.user?.username?.trim() || null;
    $[11] = row.user?.name;
    $[12] = row.user?.username;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const sellerName = t7;
  let t8;
  if ($[14] !== row.user?.username) {
    t8 = row.user?.username?.trim() || null;
    $[14] = row.user?.username;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const sellerHandle = t8;
  let t9;
  if ($[16] !== image) {
    t9 = <span className="vt-list-thumb">{image ? <Image src={image} alt="" fill={true} sizes="48px" /> : <ImageOff aria-hidden={true} />}</span>;
    $[16] = image;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== name) {
    t10 = <b>{name}</b>;
    $[18] = name;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== isFa || $[21] !== tokenId) {
    t11 = tokenDigits(tokenId, isFa);
    $[20] = isFa;
    $[21] = tokenId;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <small>#{t11}</small>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t12) {
    t13 = <span className="vt-list-name">{t10}{t12}</span>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== href || $[29] !== t13 || $[30] !== t9) {
    t14 = <Link href={href} className="vt-list-work v2-focus">{t9}{t13}</Link>;
    $[28] = href;
    $[29] = t13;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== price || $[33] !== symbol) {
    t15 = price ? <><b>{price}</b>{symbol ? <small>{symbol}</small> : null}</> : "\u2014";
    $[32] = price;
    $[33] = symbol;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <span className="vt-bid-price vt-list-price">{t15}</span>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t.columnSeller) {
    t17 = <span className="v2-sr-only">{t.columnSeller}</span>;
    $[37] = t.columnSeller;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== lang || $[40] !== row.seller || $[41] !== row.user || $[42] !== sellerHandle || $[43] !== sellerName) {
    t18 = row.user && sellerName ? sellerHandle ? <Link href={`/${lang}/artists/${encodeURIComponent(sellerHandle)}`} className="vt-list-who v2-focus"><Avatar name={sellerName} src={row.user.avatar} size={24} /><span>{sellerName}</span></Link> : <span className="vt-list-who"><Avatar name={sellerName} src={row.user.avatar} size={24} /><span>{sellerName}</span></span> : row.seller ? <FitHex value={row.seller} /> : "\u2014";
    $[39] = lang;
    $[40] = row.seller;
    $[41] = row.user;
    $[42] = sellerHandle;
    $[43] = sellerName;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t17 || $[46] !== t18) {
    t19 = <span className="vt-list-seller">{t17}{t18}</span>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== isFa || $[49] !== row.marketplace) {
    t20 = marketLabel(row.marketplace, isFa);
    $[48] = isFa;
    $[49] = row.marketplace;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t20) {
    t21 = <span className="vt-market">{t20}</span>;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== locale || $[54] !== row.timestamp) {
    t22 = row.timestamp ? <time dateTime={row.timestamp}>{dateTime(row.timestamp, locale)}</time> : null;
    $[53] = locale;
    $[54] = row.timestamp;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t21 || $[57] !== t22) {
    t23 = <span className="vt-list-meta">{t21}{t22}</span>;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t14 || $[60] !== t16 || $[61] !== t19 || $[62] !== t23) {
    t24 = <li className="vt-list">{t14}{t16}{t19}{t23}</li>;
    $[59] = t14;
    $[60] = t16;
    $[61] = t19;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  return t24;
}